import type { AxiosError, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { getTokens, setTokens } from '@rsos/sinatra';
import { getAPIHost } from '@rsos/utils/metaTags';

const STATUS_CODES = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
};

const baseURL = `${getAPIHost()}/v1/rem`;
const authURL = `${getAPIHost()}/v1/scorpius`;

// Extends the standard axios config with a custom field used to thread the
// retry flag through interceptors. Axios strips unknown AxiosRequestConfig
// fields automatically, so this is the only reliable way to pass it along.
interface MidasRequestConfig extends AxiosRequestConfig {
  custom?: { retry: boolean };
}

const opts: MidasRequestConfig = {
  baseURL,
  headers: {},
  withCredentials: true,
};

const initialTokens = getTokens();
if (initialTokens) {
  opts.headers = { Authorization: `Bearer ${initialTokens.token}` };
}

// TODO(eddie) - DRY this up with Sinatra's scorpiusInstance.js by creating
// a factory function that creates API instances that are authed via scorpius
const instance = axios.create(opts);

// When a request is sent with { headers: { retry: true } }, move the flag
// into config.custom and remove it from headers so axios doesn't forward it
// to the server.
instance.interceptors.request.use(config => {
  const midasConfig = config as MidasRequestConfig;
  const retry = midasConfig.headers?.retry;
  if (retry) {
    midasConfig.custom = { retry: true };
    delete midasConfig.headers?.retry;
  }
  return config;
});

// When a 401/403 arrives on a retryable request, refresh the access token
// and replay the original request with the new token.
instance.interceptors.response.use(
  undefined,
  async (error: AxiosError<unknown, MidasRequestConfig>) => {
    const retryCodes = [STATUS_CODES.FORBIDDEN, STATUS_CODES.UNAUTHORIZED];
    const config = error.config as MidasRequestConfig | undefined;

    const shouldRetry =
      config &&
      error.response &&
      (retryCodes as number[]).includes(error.response.status) &&
      config.custom?.retry;

    if (!shouldRetry) {
      return Promise.reject(error);
    }

    try {
      const tokens = getTokens();
      if (!tokens) throw error;

      const { data } = await axios.post<{
        token: string;
        refresh_token: string;
      }>(`${authURL}/user/api-token-auth/refresh`, {
        refresh_token: tokens.refresh_token,
      });

      const newTokens = { ...tokens, ...data };
      setTokens(newTokens);

      instance.defaults.headers.Authorization = `Bearer ${newTokens.token}`;
      if (config.headers) {
        config.headers.Authorization = `Bearer ${newTokens.token}`;
      }

      return axios.request(config);
    } catch {
      throw error;
    }
  },
);

export default instance;
